import { useEffect, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Button, Space } from '@g2u/ui-kit';
import { Alert, Form } from 'antd';

import { Content } from 'components';
import { SelectTechnician, JobDetails, Actions } from './components';
import { usePartsOnlyJob } from './hooks/usePartsOnlyJob';
import { BookingStore, RootStore } from 'store';
import { JsonDataItem } from 'types';
import { validateFormSilently } from 'helpers';
import styles from './parts-only-job.module.scss';

export const PartsOnlyJobPage: FC = observer(() => {
  const { id } = useParams();
  const { createJob, isJobCreating, createdJob, technicianList, isTechniciansLoading } = usePartsOnlyJob(Number(id));
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const goToCustomerEditing = () => navigate(`/customer/edit/${id}/details`);
  const goToJobEditing = (id: number) => navigate(`/job/edit/${id}/service-details`);
  const { setBreadcrumbs } = RootStore;
  const { customer } = BookingStore;

  const handleFormChange = () => {
    void validateFormSilently(form, setIsFormValid);
  };

  const handleFormSubmit = (formData: JsonDataItem) => {
    const { technicianId, technicianNote, description, serviceType } = formData;
    createJob({
      customerId: Number(id),
      technicianId,
      technicianNote,
      description,
      serviceType,
    });
  };

  useEffect(() => {
    setBreadcrumbs([
      {
        url: '/customer/search',
        title: 'Find a customer',
      },
      {
        url: `/customer/edit/${id}`,
        title: `Customer File ${id}`,
      },
      {
        title: 'Parts Only Job',
      },
    ]);

    return () => {
      setBreadcrumbs(null);
    };
  }, []);

  useEffect(() => {
    createdJob?.ID && goToJobEditing(createdJob?.ID);
  }, [createdJob?.ID]);

  return (
    <div className={styles.partsOnlyJob}>
      <title>Create a Parts Only Job</title>
      <Alert
        message={`Customer: ${customer?.name?.full_name}`}
        type="info"
        showIcon
        action={
          <Button.$ size={Button.Size.Small} onClick={goToCustomerEditing} dataTestId="edit-customer-btn">
            View Details
          </Button.$>
        }
      />

      <Content dataTestId="parts-only-job-page">
        <Form
          form={form}
          name="parts-only-job-form"
          layout="horizontal"
          onFinish={handleFormSubmit}
          onValuesChange={handleFormChange}
          colon={false}
          preserve={false}
        >
          <Space.$ size={Space.Size.Large} direction={Space.Direction.Vertical} fullWidth>
            <JobDetails />
            <SelectTechnician technicians={technicianList} isLoading={isTechniciansLoading} />
            <Actions
              onCancel={goToCustomerEditing}
              onConfirm={() => form.submit()}
              isCreating={isJobCreating}
              isActive={isFormValid}
            />
          </Space.$>
        </Form>
      </Content>
    </div>
  );
});
